import { useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Crown } from "lucide-react";

const METRICS = [
  { key: "overall", label: "Crown Score", max: 100 },
  { key: "hydration", label: "Hydration", max: 10 },
  { key: "buildup", label: "Buildup", max: 10 },
  { key: "irritation", label: "Irritation", max: 10 },
  { key: "density", label: "Density", max: 10 },
  { key: "dandruff", label: "Dandruff", max: 10 },
];

const GoldDot = (props) => {
  const { cx, cy } = props;
  if (cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={4} fill="#D4AF37" stroke="#0A0A0A" strokeWidth={2} />;
};

export const CrownTimeline = ({ scans }) => {
  const [metric, setMetric] = useState("overall");
  const active = METRICS.find((m) => m.key === metric);

  // scans arrive newest-first; chart ascending (oldest -> newest = ascending progress)
  const asc = [...scans].reverse();
  const data = asc.map((s, i) => ({
    name: new Date(s.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
    idx: i + 1,
    value: metric === "overall" ? s.overall_score : (s.category_scores?.[metric] ?? null),
  }));

  return (
    <div className="rounded-2xl border border-charcoal-border bg-charcoal-surface p-6 md:p-8" data-testid="crown-timeline">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-gold" />
          <h3 className="font-heading text-2xl text-cream">Your loc journey</h3>
        </div>
        <div className="flex flex-wrap gap-2">
          {METRICS.map((m) => (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                metric === m.key ? "border-gold bg-gold/10 text-gold" : "border-charcoal-border text-muted-foreground hover:text-cream"
              }`}
              data-testid={`timeline-metric-${m.key}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 16, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#262626" vertical={false} />
            <XAxis dataKey="name" tick={{ fill: "#A3A3A3", fontSize: 12 }} stroke="#262626" />
            <YAxis domain={[0, active.max]} tick={{ fill: "#A3A3A3", fontSize: 12 }} stroke="#262626" />
            <Tooltip
              contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 12, color: "#F5F5F0" }}
              labelStyle={{ color: "#D4AF37" }}
            />
            <Line type="monotone" dataKey="value" stroke="#D4AF37" strokeWidth={2.5} dot={<GoldDot />} activeDot={{ r: 6, fill: "#D4AF37" }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Tracking {active.label.toLowerCase()} across {scans.length} scan{scans.length === 1 ? "" : "s"}.</p>
    </div>
  );
};
